import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCompanionName } from '@/hooks/useCompanionName';
import { CompanionCard, usePager } from '@/components/companion/CompanionCard';
import { parseDateOnly, dateOnlyIntl } from '@/lib/safe-date';
import { msg, useMsg, type Msg, type TKey } from '@/i18n/msg';
import { formatList, formatTime, formatWeekday } from '@/i18n/intl';
import {
  formatDaysFromToday,
  isPrepSoon,
  isRefillSoon,
} from '@/lib/medical-display';
import type { FamilyPatient, MedicalAppointment, Medication, SymptomEntry } from '@/types/api';
import type { MedicalSection } from '@/lib/medical-display';
import { STEVENS_CALLOUT_DISMISSES_KEY } from '@/lib/user-scoped-storage';

/**
 * One companion observation (#2109 R3-B3): message descriptors, never English
 * sentences. The card resolves them at render, so a language switch re-reads
 * them. Dates, times and "in N days" inside the params are Intl output in the
 * language that was active when the patterns were built; HealthPage rebuilds
 * them when the language changes.
 */
export interface MedicalPattern {
  id: string;
  body: Msg;
  ctaLabel: Msg;
  /** A Health section to switch to — or, for appointments, the calendar
   *  route, since appointments live there (2026-09-09). */
  jumpTo: MedicalSection | '/calendar';
  /**
   * Optional pre-drafted question. When set, jumping to the questions tab
   * also opens the AddQuestionModal with this text prefilled — saves the
   * parent from re-typing what Stevens already saw. It is resolved in the UI
   * language at draft time (the jump); once saved it is the family's text.
   */
  draftQuestion?: Msg;
}

// Which prep sentence, by how far away the appointment is. A lookup rather
// than a `days === 1 ?` string branch: the branch is on a day offset, but the
// count-branching guard reads any `=== 1 ?` string as plural grammar.
const PREP_KEY: Record<'today' | 'tomorrow' | 'weekday', { notes: TKey; noNotes: TKey }> = {
  today:    { notes: 'health:callout.prep.today',    noNotes: 'health:callout.prep.todayNoNotes' },
  tomorrow: { notes: 'health:callout.prep.tomorrow', noNotes: 'health:callout.prep.tomorrowNoNotes' },
  weekday:  { notes: 'health:callout.prep.weekday',  noNotes: 'health:callout.prep.weekdayNoNotes' },
};

const DRAFT_KEY: Record<'severe' | 'moderate', { named: TKey; unnamed: TKey }> = {
  severe:   { named: 'health:callout.draft.severe',   unnamed: 'health:callout.draft.severeUnnamed' },
  moderate: { named: 'health:callout.draft.moderate', unnamed: 'health:callout.draft.moderateUnnamed' },
};

interface BuildArgs {
  appointments: MedicalAppointment[];
  medications: Medication[];
  symptoms: SymptomEntry[];
  patient?: FamilyPatient;
  now?: Date;
}

export function buildMedicalPatterns({
  appointments,
  medications,
  symptoms,
  patient,
  now = new Date(),
}: BuildArgs): MedicalPattern[] {
  const out: MedicalPattern[] = [];

  // Rule 1 — refill soon. The CTA opens the medications tab and says so —
  // the old "Draft refill ask" label promised a drafting step that the jump
  // does not perform (#848: CTA labels must match their destination).
  const refillSoon = medications.filter((m) => m.active && isRefillSoon(m, now));
  if (refillSoon.length > 0) {
    const med = refillSoon[0];
    const daysUntil = med.refill_date
      // Date-only (#1308) — count calendar days, or the callout says "today"
      // about a window that opens tomorrow.
      ? Math.max(0, differenceInCalendarDays(parseDateOnly(med.refill_date) ?? now, now))
      : 0;
    out.push({
      id: 'refill-soon',
      // "today" / "tomorrow" / "in 3 days" — Intl.RelativeTimeFormat, numeric: 'auto'.
      body: msg('health:callout.refill', { medication: med.medication_name, when: formatDaysFromToday(daysUntil) }),
      ctaLabel: msg('health:callout.cta.openMedications'),
      jumpTo: 'medications',
    });
  }

  // Rule 2 — prep due (an appointment in the next 48h with prep_required)
  const prepSoon = appointments
    .filter((a) => isPrepSoon(a, now))
    .sort((a, b) => parseISO(a.date_time).getTime() - parseISO(b.date_time).getTime());
  if (prepSoon.length > 0) {
    const a = prepSoon[0];
    const dt = parseISO(a.date_time);
    const days = differenceInCalendarDays(dt, now);
    const keys = PREP_KEY[days <= 0 ? 'today' : days < 2 ? 'tomorrow' : 'weekday'];
    const params: Record<string, unknown> = {
      title: a.title,
      time: formatTime(dt),
      weekday: formatWeekday(dt, { width: 'long' }),
    };
    // "Open the calendar", not "Open the appointment" — the jump lands on the
    // calendar, where appointments live, not a single record (#848: truthful CTAs).
    out.push({
      id: 'prep-soon',
      body: a.prep_notes
        ? msg(keys.notes, { ...params, notes: a.prep_notes })
        : msg(keys.noNotes, params),
      ctaLabel: msg('health:callout.cta.openCalendar'),
      jumpTo: '/calendar',
    });
  }

  // Rule 3 — symptom recurrence (≥2 moderate/severe in last 14 days unreported)
  const recentNotable = symptoms.filter((s) => {
    if (s.severity === 'mild') return false;
    if (s.reported_to_doctor) return false;
    // `s.date` is date-only (#1308): compare calendar days, or the 14-day
    // window slides by one for every reader west of UTC.
    const d = parseDateOnly(s.date);
    if (!d) return false;
    const days = differenceInCalendarDays(now, d);
    return days >= 0 && days <= 14;
  });
  if (recentNotable.length >= 2) {
    // Pick the dominant severity + a short summary of dates so the drafted
    // question is concrete enough for the parent to send as-is. Kept short
    // — the doctor doesn't need a recital, just enough to start the
    // conversation.
    const severeCount = recentNotable.filter((s) => s.severity === 'severe').length;
    const severity = severeCount > 0 ? 'severe' : 'moderate';
    // A plain date list: Intl's unit list reads "Sep 1, Sep 3, Sep 5" in English.
    const dates = formatList(
      recentNotable.slice(0, 5).map((s) => dateOnlyIntl(s.date, 'monthDay')),
      { type: 'unit', style: 'short' },
    );
    // Neutral fallback — never a gendered guess about someone's child (#848).
    const patientName = patient?.name?.trim();
    const count = recentNotable.length;
    const draftQuestion = patientName
      ? msg(DRAFT_KEY[severity].named, { name: patientName, count, dates })
      : msg(DRAFT_KEY[severity].unnamed, { count, dates });

    out.push({
      id: 'symptom-recurrence',
      body: msg('health:callout.recurrence', { count }),
      ctaLabel: msg('health:callout.cta.reviewDraft'),
      jumpTo: 'questions',
      draftQuestion,
    });
  }

  // The cycle-aware rules 4-6 (side-effect window, nadir/fever thresholds,
  // recovery window) were removed by #848 (D-G): they made clinical claims —
  // predicted symptom windows, an ER threshold — on a platform with no
  // clinical staff behind it (#845 guardrail: no clinical claims). The three
  // surviving rules only restate the family's own records.

  return out;
}

interface MedicalStevensCalloutProps {
  patterns: MedicalPattern[];
  onJump: (section: MedicalSection, opts?: { draftQuestion?: string }) => void;
  /** `card` — the right-column card (Health, 2026-09-09): stacked, one column. Rendered by the shared CompanionCard. */
  layout?: 'inline' | 'card';
}

// Map a callout id to its auto-task rule. Only callouts that have a direct
// auto-task analog get the "Have Stevens do this for me?" upsell.
const AUTO_RULE_FOR_PATTERN: Record<string, string | undefined> = {
  'refill-soon': 'refill_prep',
  'prep-soon': 'appointment_prep',
};

// Per person: dropped on every identity change (#2340).
const DISMISS_KEY = STEVENS_CALLOUT_DISMISSES_KEY;
const AUTO_PROMPT_THRESHOLD = 2;

function readDismissCounts(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}');
  } catch {
    return {};
  }
}

function bumpDismiss(id: string): number {
  const counts = readDismissCounts();
  counts[id] = (counts[id] || 0) + 1;
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify(counts)); } catch {}
  return counts[id];
}

export function MedicalStevensCallout({ patterns, onJump, layout = 'inline' }: MedicalStevensCalloutProps) {
  const { t } = useTranslation(['health', 'common']);
  const resolve = useMsg();
  const companionName = useCompanionName();
  const navigate = useNavigate();
  const [dismissCounts, setDismissCounts] = useState<Record<string, number>>(() => readDismissCounts());
  const { index, pager: rawPager } = usePager(patterns.length);
  const current = patterns.length ? patterns[index] : null;
  if (!current) return null;

  // Paging past a pattern that has an automation counts as a dismissal of it
  // (the offer to let the companion do it appears after AUTO_PROMPT_THRESHOLD).
  const noteDismissal = () => {
    if (AUTO_RULE_FOR_PATTERN[current.id]) {
      bumpDismiss(current.id);
      setDismissCounts(readDismissCounts());
    }
  };
  const pager = rawPager && {
    ...rawPager,
    onPrev: () => { noteDismissal(); rawPager.onPrev(); },
    onNext: () => { noteDismissal(); rawPager.onNext(); },
  };

  const autoRule = AUTO_RULE_FOR_PATTERN[current.id];
  const showAutoPrompt = autoRule && (dismissCounts[current.id] ?? 0) >= AUTO_PROMPT_THRESHOLD;

  const jump = () => {
    if (current.jumpTo === '/calendar') navigate('/calendar');
    // The draft is written in the UI language now, at draft time (R3-B3).
    else onJump(current.jumpTo, current.draftQuestion ? { draftQuestion: resolve(current.draftQuestion) } : undefined);
  };
  return (
    <CompanionCard layout={layout} body={resolve(current.body)} pager={pager} data-testid="medical-companion-callout">
      <Button
        size="sm"
        onClick={jump}
        variant="brand" className="h-8 text-caption"
      >
        <Plus className="h-3 w-3 mr-1" />
        {resolve(current.ctaLabel)}
      </Button>
      {showAutoPrompt && (
        <Link
          to="/settings?tab=companion#stevens-auto-tasks"
          className="inline-flex items-center h-8 px-3 rounded-md text-caption text-stevens-indigo hover:bg-stevens-indigo/10 underline-offset-4 hover:underline transition-colors"
        >
          {t('health:callout.autoOffer', { companion: companionName })}
        </Link>
      )}
    </CompanionCard>
  );
}
